import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState('EN');
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setOpenDropdown(null); // Close dropdowns on scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Organized navigation structure
  const navigation = {
    main: [
      { name: 'Home', href: '/' },
      { name: 'Schools', href: '/schools' },
    ],
    programDetails: {
      title: 'Program Details',
      items: [
        { name: 'How It Works', href: '/how-it-works' },
        { name: 'Coverage & Pricing', href: '/coverage-pricing' },
        { name: 'Urgent Care Centers', href: '/urgent-care' },
        { name: 'Investors', href: '/investors' },
        { name: 'FAQ', href: '/faq' },
      ]
    },
    about: {
      title: 'About AMHAS',
      items: [
        { name: 'Our Impact', href: '/impact' },
        { name: 'About Us', href: '/about' },
      ]
    },
    getInvolved: {
      title: 'Get Involved',
      items: [
        { name: 'Enroll Now', href: '/enroll', highlight: true },
        { name: 'For Schools', href: '/for-schools' },
        { name: 'For NGOs', href: '/for-ngos' },
        { name: 'CSR & Investors', href: '/csr-investors' },
      ]
    },
    resources: {
      title: 'Resources',
      items: [
        { name: 'Resources', href: '/resources' },
        { name: 'Contact', href: '/contact' },
      ]
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'EN' ? 'FR' : 'EN');
  };

  const handleDropdownToggle = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 w-full z-50 bg-gray-900 shadow-lg"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/amhas-logo.png"
              alt="AMHAS Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-bold text-white">AMHAS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {/* Main Links */}
            {navigation.main.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${location.pathname === item.href
                  ? 'text-primary-400 bg-white/10'
                  : 'text-white hover:text-primary-200 hover:bg-white/5'
                  }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Program Details Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('programDetails')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg flex items-center space-x-1 ${openDropdown === 'programDetails'
                  ? 'text-primary-400 bg-white/10'
                  : 'text-white hover:text-primary-200 hover:bg-white/5'
                  }`}
              >
                <span>{navigation.programDetails.title}</span>
                <ChevronDown size={16} className={`transition-transform ${openDropdown === 'programDetails' ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openDropdown === 'programDetails' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50"
                  >
                    {navigation.programDetails.items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={closeDropdown}
                        className={`block px-4 py-2 text-sm transition-colors ${location.pathname === item.href
                          ? 'text-primary-600 bg-primary-50 font-medium'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                          }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* About AMHAS Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('about')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg flex items-center space-x-1 ${openDropdown === 'about'
                  ? 'text-primary-400 bg-white/10'
                  : 'text-white hover:text-primary-200 hover:bg-white/5'
                  }`}
              >
                <span>{navigation.about.title}</span>
                <ChevronDown size={16} className={`transition-transform ${openDropdown === 'about' ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openDropdown === 'about' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50"
                  >
                    {navigation.about.items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={closeDropdown}
                        className={`block px-4 py-2 text-sm transition-colors ${location.pathname === item.href
                          ? 'text-primary-600 bg-primary-50 font-medium'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                          }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Get Involved Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('getInvolved')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg flex items-center space-x-1 ${openDropdown === 'getInvolved'
                  ? 'text-primary-400 bg-white/10'
                  : 'text-white hover:text-primary-200 hover:bg-white/5'
                  }`}
              >
                <span>{navigation.getInvolved.title}</span>
                <ChevronDown size={16} className={`transition-transform ${openDropdown === 'getInvolved' ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openDropdown === 'getInvolved' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50"
                  >
                    {navigation.getInvolved.items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={closeDropdown}
                        className={`block px-4 py-2 text-sm transition-colors ${item.highlight
                          ? 'text-primary-600 bg-primary-50 font-semibold hover:bg-primary-100'
                          : location.pathname === item.href
                            ? 'text-primary-600 bg-primary-50 font-medium'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                          }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Resources Dropdown - Disabled */}
            <div className="relative opacity-50 cursor-not-allowed">
              <button
                disabled
                className="px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg flex items-center space-x-1 text-white/50"
              >
                <span>{navigation.resources.title}</span>
                <ChevronDown size={16} />
              </button>
              <AnimatePresence>
                {openDropdown === 'resources' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50"
                  >
                    {navigation.resources.items.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={closeDropdown}
                        className={`block px-4 py-2 text-sm transition-colors ${location.pathname === item.href
                          ? 'text-primary-600 bg-primary-50 font-medium'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
                          }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Language Toggle & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Admin Button */}
            <a
              href="https://amhas.org/admin/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200 text-white hover:bg-white/10 border border-white/20 hover:border-white/40"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm font-medium">Admin</span>
            </a>

            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200 text-white hover:bg-white/10"
            >
              <Globe size={16} />
              <span className="text-sm font-medium">{language}</span>
            </button>

            <Link
              to="/student-enrollment"
              className="btn-primary text-sm py-2 px-6"
            >
              Enroll Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-gray-800 border-t border-gray-700"
          >
            <div className="container-custom py-4 space-y-2">
              {/* Main Links */}
              {navigation.main.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === item.href
                    ? 'text-primary-400 bg-white/10'
                    : 'text-white hover:bg-white/5'
                    }`}
                >
                  {item.name}
                </Link>
              ))}

              {/* Program Details Section */}
              <div className="pt-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {navigation.programDetails.title}
                </div>
                {navigation.programDetails.items.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm transition-colors ${location.pathname === item.href
                      ? 'text-primary-400 bg-white/10'
                      : 'text-gray-300 hover:bg-white/5'
                      }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* About Section */}
              <div className="pt-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {navigation.about.title}
                </div>
                {navigation.about.items.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm transition-colors ${location.pathname === item.href
                      ? 'text-primary-400 bg-white/10'
                      : 'text-gray-300 hover:bg-white/5'
                      }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Get Involved Section */}
              <div className="pt-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {navigation.getInvolved.title}
                </div>
                {navigation.getInvolved.items.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm transition-colors ${item.highlight
                      ? 'text-primary-400 bg-primary-900/30 font-semibold'
                      : location.pathname === item.href
                        ? 'text-primary-400 bg-white/10'
                        : 'text-gray-300 hover:bg-white/5'
                      }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Resources Section - Disabled */}
              <div className="pt-2 opacity-50">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {navigation.resources.title} (Coming Soon)
                </div>
                {navigation.resources.items.map((item) => (
                  <div
                    key={item.name}
                    className="block px-4 py-3 text-sm text-gray-400 cursor-not-allowed"
                  >
                    {item.name}
                  </div>
                ))}
              </div>

              {/* Admin Button */}
              <div className="pt-4 px-4">
                <a
                  href="https://amhas.org/admin/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors border border-white/20"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-sm font-medium">Admin Portal</span>
                </a>
              </div>

              {/* Language Toggle */}
              <div className="pt-2 px-4">
                <button
                  onClick={toggleLanguage}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  <Globe size={16} />
                  <span className="text-sm font-medium">{language}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
